/**
 * AI:
 * Status: 'in progress'
 * Type: Component
 * SubType: CrudIntegration
 * Reason: Dashboard component for JbJobRun entity displaying stats cards grouped by jobStatus and a QL widget table (read-only, no create)
 */
import {Component, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {WidgetData} from '../../../../../core/shared/widget/widget-data';
import {WidgetFactory} from '../../../../../core/shared/widget/service/widget.factory';
import {ObjectRequestListGroupBy} from '../../../../../core/shared/dto/object/request/object-request-list-group-by';
import {ObjectRequestListAttribute} from '../../../../../core/shared/dto/object/request/object-request-list-attribute';
import {
    DtoListAttributeRequestAggregateEnum
} from '../../../../../core/shared/dto/enum/dto-list-attribute-request-aggregate.enum';
import {Sorting} from '../../../../../core/shared/misc/sorting';
import {FilterCriteria} from '../../../../../core/shared/filter/api/filter.criteria';
import {ObjectIdentifierData} from '../../../../../core/shared/basic/object-identifier-data';
import {ObjectIdentifier} from '../../../../../core/shared/basic/object-identifier';
import {MvsObjectNavigationService} from '../../../../../core/shared/navigation/mvs-object-navigation.service';
import {MvsObjectNavigationEntry} from '../../../../../core/shared/navigation/mvs-object-navigation-entry';
import {MvsObjectNavigationActionEnum} from '../../../../../core/shared/navigation/mvs-object-navigation-action-enum';
import {JbJobRunService} from '../../../service/api/jb-job-run.service';
import {JobRunStat} from '../../../interface/job-run-stat.interface';

@Component({
    selector: 'jb-job-run-dashboard',
    templateUrl: 'jb-job-run-dashboard.component.html',
    standalone: false
})
export class JbJobRunDashboardComponent implements OnInit, OnChanges, OnDestroy {

    busy: boolean;
    initialized: boolean;

    stats: JobRunStat[] = [];
    jobRunTableWidget: WidgetData;

    constructor(
        protected navigationService: MvsObjectNavigationService,
        protected jobRunService: JbJobRunService
    ) {
    }

    ngOnInit(): void {
        this.initComponent();
    }

    /**
     * Initialize Component.
     */
    initComponent(): void {
        this.refreshComponent();
    }

    /**
     * Refresh Component.
     */
    refreshComponent(): void {
        this.refreshStats();
        this.refreshJobRunTableWidget();
        this.initialized = true;
    }

    /**
     * Refresh stats by querying JbJobRun grouped by jobStatus for the last 24 hours.
     */
    refreshStats(): void {
        const now = new Date();
        const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000);

        const last24hFilter = FilterCriteria.create('startTime', FilterCriteria.cOperatorGreaterEqual, yesterday.toISOString());

        this.jobRunService.groupBy(
            ObjectRequestListGroupBy.create(
                false,
                [last24hFilter],
                [],
                ['jobStatus'],
                [new ObjectRequestListAttribute('jobStatus', 'Count', DtoListAttributeRequestAggregateEnum.count)]
            )
        ).subscribe(result => {
            let total = 0;
            let completed = 0;
            let errorCount = 0;
            let skipped = 0;

            if (result && result.entries) {
                for (const entry of result.entries) {
                    const count = entry['jobStatus_count'] || 0;
                    const status = entry['jobStatus'];
                    total += count;

                    // JbJobStatusEnum ordinals: RUNNING=0, COMPLETED=1, ERROR=2, SKIPPED=3
                    if (status === 1) {
                        completed = count;
                    } else if (status === 2) {
                        errorCount = count;
                    } else if (status === 3) {
                        skipped = count;
                    }
                }
            }

            this.stats = [
                {
                    label: 'Läufe gesamt (24h)',
                    value: total,
                    icon: 'fa-regular fa-list-check',
                    colorClass: 'text-primary'
                },
                {
                    label: 'Abgeschlossen',
                    value: completed,
                    icon: 'fa-regular fa-circle-check',
                    colorClass: 'text-green-500'
                },
                {
                    label: 'Fehler',
                    value: errorCount,
                    icon: 'fa-regular fa-circle-xmark',
                    colorClass: 'text-red-500'
                },
                {
                    label: 'Übersprungen',
                    value: skipped,
                    icon: 'fa-regular fa-forward',
                    colorClass: 'text-orange-500'
                }
            ];
        });
    }

    /**
     * Refresh the main JbJobRun table widget with FK resolution and startTime DESC sorting.
     */
    refreshJobRunTableWidget(): void {
        /**
         * AI:
         * Status: 'in progress'
         * Type: Widget
         * SubType: Runtime
         * Reason: JbJobRun table widget with FK resolution for browsing all job runs sorted by startTime descending
         */
        this.jobRunTableWidget = WidgetFactory.createWidgetTableEntityFkeyResolveQl(
            'jb.dashboard.jobRun.table.main',
            'Job-Läufe',
            'jb.JbJobRun',
            [],
            [new Sorting('e.startTime', false)]
        );
    }

    /**
     * Handle row click to open JbJobRun in the side navigation panel.
     */
    handleObjectSelect(object: ObjectIdentifierData | ObjectIdentifier): void {
        const navigationEntry = MvsObjectNavigationEntry.createNavigationEntry(
            object.objectType,
            object.objectId,
            'object',
            null,
            null,
            null,
            MvsObjectNavigationActionEnum.any,
            'side'
        );
        this.navigationService.navigateTo(navigationEntry, 'right');
    }

    /**
     * Process changes within Binding.
     * @param changes
     */
    ngOnChanges(changes: SimpleChanges): void {
        if (!this.initialized) {
            return;
        }

        this.refreshComponent();
    }

    /**
     * Destroy component.
     */
    ngOnDestroy(): void {
    }
}
